import axios from "axios";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import MediaQuery from "react-responsive";
import { useHistory, useParams } from "react-router-dom";
import { DotLoaderOverlay } from "react-spinner-overlay";
import * as io from "socket.io-client";
import Select from "react-select";
import { ReactComponent as ChevronLeft } from "../../assets/icons/chevron-left.svg";
import { ReactComponent as ChevronRight } from "../../assets/icons/chevron-right.svg";
import { ReactComponent as Clock } from "../../assets/icons/clock.svg";
import { ReactComponent as SpecialNiplIcon } from "../../assets/icons/honour-fill.svg";
import { ReactComponent as Pause } from "../../assets/icons/pause-circle-line.svg";
import { ReactComponent as Play } from "../../assets/icons/play-circle-line.svg";
import { ReactComponent as Stop } from "../../assets/icons/stop-circle-line.svg";
import Button from "../../components/Button";
import Modal from "../../components/Modal";
import ModalHentikanLelang from "../../components/ModalHentikanLelang";
import ModalPilihPemenang from "../../components/ModalPilihPemenang";
import SocketStatusBadge from "../../components/SocketStatusBadge";
import ToggleSwitch from "../../components/Switch";
import { COLORS } from "../../constants/colors";
import useSocketConnection from "../../hook/useSocketConnection";
import {
  formatDate,
  formatRupiah,
  getLastObject,
} from "../../helpers/functionFormat";
import { finishSold, getOfflineNipl } from "../../redux/actions";

const initialModal = {
  title: "",
  buttonText: "",
  body: "",
  show: false,
  onSubmit: () => {},
};

const initialModalPemenang = {
  body: [],
  show: false,
  onSubmit: (buyer_nipl_id) => {},
};

const initialModalHentikan = {
  body: [],
  show: false,
  type: "",
  onSubmit: (buyer_nipl_id) => {},
};

// UI Constants
const AUCTION_STATUS = {
  COMING: "COMING",
  STARTED: "STARTED",
  PENDING: "PENDING",
  CLOSED: "CLOSED",
};

// Batas tunggu SetAutopilotResponse sebelum overlay loading dilepas paksa
const AUTOPILOT_RESPONSE_TIMEOUT_MS = 10000;

const BIDDER_TYPES = {
  OFFLINE: "Offline Bidder",
  ONLINE: "Online Bidder",
};

/**
 * V2LiveBiddingGo Component
 *
 * A comprehensive live bidding auction component that handles real-time auction management,
 * socket communication, and user interactions for conducting auctions.
 *
 * Features:
 * - Real-time socket communication for auction events
 * - Live countdown timer
 * - Bid management and proxy bidding
 * - Autopilot mode
 * - Manual lot selection and management
 * - Accessibility support with ARIA labels
 *
 * @component
 * @returns {JSX.Element} The rendered live bidding interface
 */
const V2LiveBiddingGo = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();

  // Redux state
  const token = useSelector((state) => state.auth.token);
  const tokenType = useSelector((state) => state.auth.tokenType);
  const url = useSelector((state) => state.config.url);

  // Local state
  const [modals, setModals] = useState({
    main: { ...initialModal },
    pemenang: { ...initialModalPemenang },
    hentikan: { ...initialModalHentikan },
  });

  const [auctionData, setAuctionData] = useState({
    data: {},
    auction: {},
    currentUnit: null,
    nextLot: [],
    notSell: [],
    bidder: [],
    proxyBidder: [],
    nextBid: 0,
    multipleBid: 0,
    status: "COMING",
    countDown: { minutes: 0, seconds: 0, run: false },
    lastLot: false,
  });

  const [uiState, setUiState] = useState({
    loading: false,
    isAutopilot: false,
    buttonDisabled: false,
    disabledHentikan: true,
    curIndex: 0,
    idLotManual: "",
    currentBidderType: "",
    key: 1,
    initialLoading: true,
    bidButtonCooldown: false,
    startLoading: false,
  });

  // Next lots API state
  const [nextLotsLoading, setNextLotsLoading] = useState(false);
  const [nextLotsPage, setNextLotsPage] = useState(1);
  const [nextLotsTotalPage, setNextLotsTotalPage] = useState(1);
  const nextLotsPageRef = useRef(1);
  const currentNextLotSlideRef = useRef(0);

  // Not sell pagination state
  const [notSellLoading, setNotSellLoading] = useState(false);
  const [notSellPage, setNotSellPage] = useState(1);
  const [notSellTotalPage, setNotSellTotalPage] = useState(1);

  // Refs
  const intervalCountDown = useRef(null);
  const previousUnitIdRef = useRef(null);
  const lastWinnerIdRef = useRef(null); // null = belum ada, 0 = tidak terjual, >0 = terjual
  const lastBidderCountRef = useRef(0); // jumlah bidder unit sebelumnya
  const autopilotTimeoutRef = useRef(null); // null = tidak ada toggle autopilot yang menunggu respons

  const clearAutopilotTimeout = () => {
    if (!autopilotTimeoutRef.current) return;
    clearTimeout(autopilotTimeoutRef.current);
    autopilotTimeoutRef.current = null;
  };

  // Constants
  const SOCKET_URL = process.env.REACT_APP_SOCKET;
  const ACCESS_TOKEN = `Bearer ${token}`;
  const AUCTION_SOCKET_ROOM = `auction:${id}`;

  const socketRef = useRef(null);

  /**
   * Displays an error toast notification with the given error message
   * @param {string} error - The error message to display
   */
  /**
   * Displays an error modal with the given error message
   * @param {string|Object} error - The error message to display
   */
  const showError = (error) => {
    const errorMessage =
      typeof error === "string"
        ? error
        : error?.message ||
          error?.data?.message ||
          JSON.stringify(error) ||
          "Terjadi kesalahan";

    setModals((prev) => ({
      ...prev,
      main: {
        show: true,
        title: "Terjadi Kesalahan",
        body: errorMessage,
        buttonText: "OK",
        cancelText: false,
        onSubmit: () =>
          setModals((prev) => ({
            ...prev,
            main: { ...prev.main, show: false },
          })),
      },
    }));
    setUiState((prev) => ({ ...prev, loading: false, startLoading: false }));
  };

  /**
   * Emits a socket event with error handling
   * @param {string} eventName - The name of the socket event
   * @param {Object} payload - The payload to send with the event
   */
  const emitSocketEvent = (eventName, payload) => {
    try {
      console.log(`📤 Emitting ${eventName}:`, payload);
      socket.emit(eventName, payload);
    } catch (error) {
      console.log(`❌ Error emitting ${eventName}:`, error);
      showError(error);
    }
  };

  /**
   * Shows a confirmation modal with the given title and callback
   * @param {string} title - The title of the confirmation modal
   * @param {Function} onSubmit - The callback function to execute on confirmation
   */
  const showConfirmation = (title, onSubmit) => {
    setModals((prev) => ({
      ...prev,
      main: {
        show: true,
        title,
        body: "",
        buttonText: "OK",
        cancelText: true,
        onSubmit: () => {
          onSubmit();
          setModals((prev) => ({
            ...prev,
            main: { ...prev.main, show: false },
          }));
        },
      },
    }));
  };

  if (!socketRef.current) {
    socketRef.current = io(SOCKET_URL, {
      query: { token: ACCESS_TOKEN },
      transports: ["websocket"],
      autoConnect: true,
      reconnection: true,
      // Konduktor harus selalu tersambung: coba terus (useSocketConnection
      // menampilkan modal bila lebih dari 1 menit belum tersambung).
      reconnectionAttempts: Infinity,
      reconnectionDelay: 2000,
    });
  }

  const socket = socketRef.current;

  /**
   * Converts seconds to time object with minutes, seconds, and run status
   * @param {number} secs - The number of seconds to convert
   * @param {string} currentStatus - Optional current status to check
   * @returns {Object} Object containing minutes, seconds, and run status
   */
  const secondsToTime = useCallback(
    (secs, currentStatus = null) => {
      let divisor_for_minutes = secs % (60 * 60);
      let minutes = Math.floor(divisor_for_minutes / 60);
      let divisor_for_seconds = divisor_for_minutes % 60;
      let seconds = Math.ceil(divisor_for_seconds);
      let run = secs !== 0;
      let obj = { minutes, seconds, run };

      // Use provided status or fall back to auctionData.status
      const statusToCheck = currentStatus || auctionData.status;

      if (statusToCheck === AUCTION_STATUS.PENDING) {
        return { ...auctionData.countDown, run: false };
      }
      return obj;
    },
    [auctionData.status, auctionData.countDown],
  );

  /**
   * Auction action handlers - memoized for performance
   * @type {Object}
   */
  const auctionActions = useMemo(
    () => ({
      start: () =>
        emitSocketEvent("StartAuction", { room: AUCTION_SOCKET_ROOM }),

      pending: (stat = null, currentStatus = null) => {
        const statusToCheck = currentStatus || auctionData.status;
        const newStatus =
          stat ||
          (statusToCheck === AUCTION_STATUS.PENDING
            ? AUCTION_STATUS.STARTED
            : AUCTION_STATUS.PENDING);

        // Guard condition: prevent duplicate emit if status is already the same
        if (statusToCheck === newStatus) {
          console.log(
            `⚠️ Pending action skipped - status already ${newStatus}`,
          );
          return;
        }
        console.log("test", statusToCheck);
        // Guard condition: prevent emitting pending when auction status is COMING
        if (statusToCheck === AUCTION_STATUS.COMING) {
          console.log(
            `⚠️ Pending action skipped - auction status is COMING, cannot emit pending lelang`,
          );
          return;
        }
        // Guard condition: prevent emitting pending when auction status is CLOSED
        if (statusToCheck === AUCTION_STATUS.CLOSED) {
          console.log(
            `⚠️ Pending action skipped - auction status is CLOSED, cannot emit pending lelang`,
          );
          return;
        }

        console.log(
          `⏸️ Pending action - Current status: ${statusToCheck}, New status: ${newStatus}`,
        );
        emitSocketEvent("Pending", {
          room: AUCTION_SOCKET_ROOM,
          status: newStatus,
        });
        if (newStatus === AUCTION_STATUS.STARTED) {
          console.log("▶️ Auction resumed - enabling stop button");
          setUiState((prev) => ({ ...prev, disabledHentikan: false }));
        } else {
          console.log("⏸️ Auction paused - disabling stop button");
          setUiState((prev) => ({ ...prev, disabledHentikan: true }));
        }
      },

      bid: () =>
        emitSocketEvent("SendBid", {
          room: AUCTION_SOCKET_ROOM,
          offer: auctionData.nextBid,
          unit_id: auctionData.currentUnit?.id,
          is_conductor: true,
        }),

      sold: (niplId = 0) => {
        lastWinnerIdRef.current = niplId > 0 ? niplId : 1; // >0 = terjual
        emitSocketEvent("NextLot", {
          room: AUCTION_SOCKET_ROOM,
          winner_id: niplId,
        });
        setModals((prev) => ({
          ...prev,
          main: {
            show: true,
            // title: "Unit Terjual",
            body: "Unit berhasil terjual.",
            buttonText: "OK",
            cancelText: false,
            onSubmit: () =>
              setModals((prev) => ({
                ...prev,
                main: { ...prev.main, show: false },
              })),
          },
        }));

        // Emit pending if next_lot is null
        if (auctionData.nextLot === null || auctionData.nextLot?.length === 0) {
          console.log(
            "📌 next_lot is null/empty - emitting pending after sold",
          );
          auctionActions.pending(AUCTION_STATUS.PENDING, auctionData.status);
        }
      },

      notSold: () => {
        // Server re-checks bidder data fresh and is the source of truth — it
        // no longer trusts this client's local bidder count. If it still has
        // bidder(s) on record, it responds with NextLotConfirmationRequired
        // instead of executing, so a stale local list here can't silently
        // override a real sale.
        lastWinnerIdRef.current = 0; // 0 = tidak terjual
        emitSocketEvent("NextLot", {
          room: AUCTION_SOCKET_ROOM,
          winner_id: 0,
          force_not_sold: true,
        });
        setModals((prev) => ({
          ...prev,
          main: {
            show: true,
            // title: "Unit Tidak terjual",
            body: "Unit tidak terjual.",
            buttonText: "OK",
            cancelText: false,
            onSubmit: () =>
              setModals((prev) => ({
                ...prev,
                main: { ...prev.main, show: false },
              })),
          },
        }));

        // Emit pending if next_lot is null
        if (
          auctionData.nextLot === null ||
          auctionData.nextLot?.length === 0
        ) {
          console.log(
            "📌 next_lot is null/empty - emitting pending after not sold",
          );
          auctionActions.pending(AUCTION_STATUS.PENDING, auctionData.status);
        }
      },

      stop: () =>
        emitSocketEvent("StopAuction", {
          room: AUCTION_SOCKET_ROOM,
          status: AUCTION_STATUS.CLOSED,
        }),

      resell: (id) => {
        emitSocketEvent("ReSellNotSoldUnit", {
          room: AUCTION_SOCKET_ROOM,
          unit_id: id || uiState.idLotManual,
        });
        auctionActions.pending(AUCTION_STATUS.STARTED, auctionData.status);
      },

      join: () => emitSocketEvent("JoinAuction", { room: AUCTION_SOCKET_ROOM }),

      floor: () => emitSocketEvent("Floor", { room: AUCTION_SOCKET_ROOM }),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      AUCTION_SOCKET_ROOM,
      auctionData.status,
      auctionData.nextBid,
      auctionData.currentUnit?.id,
      auctionData.bidder?.length,
      uiState.idLotManual,
      auctionData.nextLot,
    ],
  );

  /**
   * Handles incoming socket auction data and updates component state
   * @param {Object} data - The auction data received from socket
   */
  const handleSocketAuction = (data, eventName = "", extra = null) => {
    const currentAuction = data?.auction;

    // Store multiple bid value when available
    const multipleBidValue = currentAuction?.unit_type?.multiple_bid || 0;

    // CRITICAL: Special handling for SendBidResponse
    // SendBidResponse should ONLY update bidders list and countdown
    // It should NOT update currentUnit to prevent "Tidak ada lot tersedia" message
    if (eventName === "SendBidResponse") {
      console.log("🔄 SendBidResponse - Updating only bidders and countdown");

      const hasCurrentUnit = data?.current_unit && data.current_unit.length > 0;

      if (hasCurrentUnit) {
        const currentUnit = data?.current_unit?.[0] || null;
        const bidders =
          currentUnit?.bidders
            ?.slice()
            ?.reverse()
            ?.map((el) => ({
              ...el,
              offer_price: parseInt(el.offer),
              type: el.email,
            })) || [];

        const proxyBids = currentUnit?.proxy_bids || [];

        // Update lastBidderCountRef agar autopilot modal bisa baca jumlah bidder terkini
        lastBidderCountRef.current = bidders.length;
        console.log("📊 SendBidResponse - updated lastBidderCountRef:", bidders.length);

        // Use stored multipleBid from state instead of trying to get it from response
        setAuctionData((prev) => {
          const storedMultipleBid = prev.multipleBid || 0;
          const nextBidValue = currentUnit?.current_price
            ? currentUnit?.current_price + storedMultipleBid
            : bidders.length > 0
              ? bidders[0].offer_price + storedMultipleBid
              : currentUnit?.base_price;

          return {
            ...prev,
            bidder: bidders,
            proxyBidder: proxyBids,
            nextBid: nextBidValue,
          };
        });

        setUiState((prev) => ({
          ...prev,
          currentBidderType: bidders?.[0]?.type || null,
          key: prev.key + 1,
        }));
      }

      // Update countdown for SendBidResponse
      // Use status from state (auctionData.status) since SendBidResponse might not include auction field
      if (
        data?.seconds_left_before !== undefined &&
        data?.seconds_left_before !== null
      ) {
        // Use auctionData.status from state, not from response (which might be undefined)
        const statusToUse = auctionData?.status || AUCTION_STATUS.STARTED;
        const countDownData = secondsToTime(
          data.seconds_left_before,
          statusToUse,
        );

        setAuctionData((prev) => ({ ...prev, countDown: countDownData }));
      } else {
        console.log("⚠️ SendBidResponse - No seconds_left_before in response");
      }

      // Exit early - don't process the rest of the function
      return;
    }

    // CRITICAL: Special handling for SetAutopilotResponse / AutopilotResponse
    // Must NOT overwrite currentUnit/bidder/proxyBidder/nextBid. The toggle
    // request/response round-trip can take a few seconds, during which a
    // real bid can arrive and update those exact fields via
    // SendBidResponse. If this event's (possibly stale) current_unit
    // snapshot then overwrote them, the bid would silently vanish from the
    // operator's screen even though the server never lost it. Only the
    // autopilot flag + auction status actually need updating here.
    if (
      eventName === "SetAutopilotResponse" ||
      eventName === "AutopilotResponse"
    ) {
      console.log(
        `🔄 ${eventName} - Updating only isAutopilot, not bidder/currentUnit`,
      );

      setAuctionData((prev) => ({
        ...prev,
        auction: currentAuction,
        status: currentAuction?.status,
      }));

      // Cabang ini return lebih awal, jadi loading harus dilepas di sini —
      // kalau tidak, overlay baru hilang saat ada event lain (mis. NextLot).
      clearAutopilotTimeout();
      setUiState((prev) => ({
        ...prev,
        loading: false,
        isAutopilot: currentAuction?.is_autopilot === 1,
        disabledHentikan:
          currentAuction?.status === AUCTION_STATUS.COMING ||
          currentAuction?.status === AUCTION_STATUS.CLOSED,
      }));

      return;
    }

    // Check if autopilot is on and this is NextLotResponse
    if (eventName === "NextLotResponse" && currentAuction?.is_autopilot === 1) {
      // Get previous unit ID from ref
      const previousUnitId = previousUnitIdRef.current;

      if (previousUnitId) {
        const newCurrentUnitId = data?.current_unit?.[0]?.id || null;
        const unitHasChanged = newCurrentUnitId !== previousUnitId;

        if (unitHasChanged) {
          // Autopilot: terjual jika ada bidder, tidak terjual jika tidak ada bidder
          const isNotSold = lastBidderCountRef.current === 0;

          // Reset refs setelah dipakai
          lastWinnerIdRef.current = null;

          if (isNotSold) {
            console.log("⚠️ Autopilot: Unit tidak terjual - showing modal");
            setModals((prev) => ({
              ...prev,
              main: {
                show: true,
                body: "Unit tidak terjual.",
                buttonText: "OK",
                cancelText: false,
                onSubmit: () =>
                  setModals((prev) => ({
                    ...prev,
                    main: { ...prev.main, show: false },
                  })),
              },
            }));
          } else {
            console.log("✅ Autopilot: Unit terjual - showing modal");
            setModals((prev) => ({
              ...prev,
              main: {
                show: true,
                body: "Unit berhasil terjual.",
                buttonText: "OK",
                cancelText: false,
                onSubmit: () =>
                  setModals((prev) => ({
                    ...prev,
                    main: { ...prev.main, show: false },
                  })),
              },
            }));
          }
        }
      }
    }

    // NOTE: listLot / nextLot / notSell are now managed via API (fetchNextLots).
    // Socket data is only used for auction metadata, current unit, bidders, and countdown.

    // Update auction data (preserve listLot/nextLot/notSell from API)
    setAuctionData((prev) => ({
      ...prev,
      data,
      auction: currentAuction,
      status: currentAuction?.status,
      multipleBid: multipleBidValue, // Store multiple bid value
    }));

    // Update UI state
    setUiState((prev) => ({
      ...prev,
      loading: false,
      initialLoading: false,
      isAutopilot: currentAuction?.is_autopilot === 1,
      disabledHentikan:
        currentAuction?.status === AUCTION_STATUS.COMING ||
        currentAuction?.status === AUCTION_STATUS.CLOSED,
      buttonDisabled: data?.current_unit?.number_of_nipl > 1,
    }));

    // Handle current unit
    // current_unit is an array, so check if it has items
    const hasCurrentUnit = data?.current_unit && data.current_unit.length > 0;

    let currentUnit = null;
    if (hasCurrentUnit) {
      // Use currentAuction.status consistently (data.status doesn't exist in response)
      if (
        currentAuction?.status === AUCTION_STATUS.CLOSED &&
        auctionData.bidder.length > 0
      ) {
        setFinishSold(id);
      }
      if (currentAuction?.status !== AUCTION_STATUS.CLOSED) {
        currentUnit = data?.current_unit?.[0] || null;
        const bidders =
          currentUnit?.bidders
            ?.slice()
            ?.reverse()
            ?.map((el) => ({
              ...el,
              offer_price: parseInt(el.offer),
              type: el.email,
            })) || [];

        const proxyBids = currentUnit?.proxy_bids || [];

        // Store current unit ID in ref for next lot comparison
        if (currentUnit?.id) {
          // Simpan jumlah bidder unit saat ini sebelum pindah ke unit berikutnya
          lastBidderCountRef.current = bidders.length;
          previousUnitIdRef.current = currentUnit.id;
          console.log("💾 Stored previous unit ID:", currentUnit.id, "bidders:", bidders.length);
        }

        setAuctionData((prev) => ({
          ...prev,
          currentUnit,
          bidder: bidders,
          proxyBidder: proxyBids,
          nextBid: currentUnit?.current_price
            ? currentUnit?.current_price + multipleBidValue
            : bidders.length > 0
              ? bidders[0].offer_price + multipleBidValue
              : currentUnit?.base_price,
        }));

        setUiState((prev) => ({
          ...prev,
          currentBidderType: bidders?.[0]?.type || null,
          key: prev.key + 1,
        }));
      } else {
        // When auction is closed, set currentUnit to null
        currentUnit = null;
        setAuctionData((prev) => ({
          ...prev,
          currentUnit: null,
          bidder: [],
          proxyBidder: [],
          nextBid: 0,
        }));
        // Note: Don't call pending here - auction is already CLOSED, not STARTED
        // The UI will show "Lelang Telah Selesai" based on status
      }
    } else {
      // No current unit - check if there are next lots or not_sell lots available
      setAuctionData((prev) => ({
        ...prev,
        currentUnit: null,
        bidder: [],
        proxyBidder: [],
        nextBid: 0,
      }));
    }

    // Update countdown - ALWAYS use seconds_left_before from response when auction is STARTED
    // Don't rely on currentUnit variable which might be stale
    // Pass currentAuction.status to secondsToTime to ensure correct status check
    const shouldRunCountdown =
      currentAuction?.status === AUCTION_STATUS.STARTED &&
      hasCurrentUnit &&
      data?.seconds_left_before !== undefined;

    const countDownData = shouldRunCountdown
      ? secondsToTime(data.seconds_left_before, currentAuction?.status)
      : secondsToTime(0, currentAuction?.status);

    console.log("⏱️ Countdown update:", {
      eventName,
      status: currentAuction?.status,
      hasCurrentUnit,
      seconds_left_before: data?.seconds_left_before,
      shouldRunCountdown,
      countDownData,
    });

    setAuctionData((prev) => ({ ...prev, countDown: countDownData }));
  };

  /**
   * Toggles autopilot mode for the auction
   */
  const toggleAutopilot = useCallback(() => {
    clearAutopilotTimeout();
    setUiState((prev) => ({ ...prev, loading: true }));
    // Fallback bila respons tidak pernah datang (mis. socket reconnect di
    // tengah proses): lepas overlay dan minta konduktor cek status autopilot.
    autopilotTimeoutRef.current = setTimeout(() => {
      autopilotTimeoutRef.current = null;
      showError(
        "Tidak ada respons dari server saat mengubah autopilot. Periksa status autopilot lalu coba lagi."
      );
    }, AUTOPILOT_RESPONSE_TIMEOUT_MS);
    socket.emit("SetAutoPilot", {
      room: AUCTION_SOCKET_ROOM,
      auto_pilot: !uiState.isAutopilot ? 1 : 0,
    });
  }, [socket, AUCTION_SOCKET_ROOM, uiState.isAutopilot]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => clearAutopilotTimeout();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Manages auction actions (start, finish, pending)
   * @param {string} action - The action to perform ('start', 'finish', 'pending')
   * @param {number|null} id - Optional lot ID for resell actions
   */
  const manageAuction = useCallback(
    (action, id = null) => {
      switch (action) {
        case "start":
          if (id) {
            auctionActions.resell(parseInt(id));
          } else {
            setUiState((prev) => ({ ...prev, startLoading: true }));
            auctionActions.start();
          }
          break;
        case "finish":
          auctionActions.stop();
          setAuctionData((prev) => ({
            ...prev,
            status: AUCTION_STATUS.CLOSED,
          }));
          setUiState((prev) => ({
            ...prev,
            disabledHentikan: true,
          }));
          // Disconnect websocket when auction is stopped
          console.log("🛑 Disconnecting websocket - auction stopped");
          if (socket && socket.connected) {
            socket.disconnect();
          }
          // // Redirect to riwayat page after stopping auction
          // setTimeout(() => {
          //   history.push("/riwayat");
          // }, 1000);
          break;
        case "pending":
          setUiState((prev) => ({ ...prev, loading: true }));
          auctionActions.pending();
          break;
        default:
          break;
      }
    },
    [auctionActions, socket],
  );

  const setFinishSold = async (auctionId) => {
    const listBidderRes = await dispatch(getOfflineNipl(auctionId));
    if (listBidderRes?.data?.status_code === 401) {
      history.push("/login");
      window.location.reload();
    }
    if (listBidderRes.status === "error") {
      setUiState((prev) => ({ ...prev, loading: false }));
      showError(listBidderRes.data);
    } else {
      setModals((prev) => ({
        ...prev,
        hentikan: {
          ...prev.hentikan,
          show: true,
          body: listBidderRes.data.data,
          type: "OFFLINE",
          onSubmit: (buyer_nipl_id) => onFinishSold(buyer_nipl_id),
        },
      }));
    }
  };

  /**
   * Executes auction actions (bid, floor, notSold)
   * @param {string} action - The action to execute ('bid', 'floor', 'notSold')
   * @param {boolean} withLoading - Whether to show loading state
   */
  const executeAction = useCallback(
    (action, withLoading = false) => {
      if (withLoading) {
        setUiState((prev) => ({ ...prev, loading: true }));
      }

      switch (action) {
        case "bid":
          // Set cooldown untuk button bid
          setUiState((prev) => ({ ...prev, bidButtonCooldown: true }));
          auctionActions.bid();
          // Reset cooldown setelah 1 detik
          setTimeout(() => {
            setUiState((prev) => ({ ...prev, bidButtonCooldown: false }));
          }, 1000);
          break;
        case "floor":
          auctionActions.floor();
          break;
        case "notSold":
          auctionActions.notSold();
          break;
        default:
          break;
      }
    },
    [auctionActions],
  );

  const onSold = async () => {
    if (uiState.currentBidderType === BIDDER_TYPES.OFFLINE) {
      const listBidderRes = await dispatch(getOfflineNipl(id));
      if (listBidderRes?.data?.status_code === 401) {
        history.push("/login");
        window.location.reload();
      }
      if (listBidderRes.status === "error") {
        setUiState((prev) => ({ ...prev, loading: false }));
        showError(listBidderRes.data);
      } else {
        setModals((prev) => ({
          ...prev,
          pemenang: {
            ...prev.pemenang,
            show: true,
            body: listBidderRes.data.data,
            onSubmit: (buyer_nipl_id) => {
              setUiState((prev) => ({ ...prev, loading: true }));
              auctionActions.sold(buyer_nipl_id);
              setModals((prev) => ({
                ...prev,
                pemenang: { ...prev.pemenang, show: false },
              }));
            },
          },
        }));
      }
    } else {
      setUiState((prev) => ({ ...prev, loading: true }));
      auctionActions.sold(0);
    }
  };

  const onFinishSold = async (buyer_nipl_id) => {
    const response = await dispatch(finishSold(id, buyer_nipl_id));
    if (response?.data?.status_code === 401) {
      history.push("/login");
      window.location.reload();
    }
    if (response.status === "error") {
      setUiState((prev) => ({ ...prev, loading: false }));
      showError(response.data);
    } else {
      setModals((prev) => ({
        ...prev,
        hentikan: { ...prev.hentikan, show: true, body: "", type: "SOLD" },
      }));
    }
  };

  /**
   * Handles lot selection change in manual lot dropdown
   * @param {Object} selectedOption - The selected option from react-select
   */
  const onChangeLot = useCallback((selectedOption) => {
    setUiState((prev) => ({
      ...prev,
      idLotManual: selectedOption?.value || "",
    }));
  }, []);

  /**
   * Fetches next lot list from API with pagination (excludes not-sold lots)
   * @param {number} page - Page number to fetch
   * @param {boolean} append - Whether to append to existing list or replace
   */
  const fetchNextLots = useCallback(
    async (page = 1, append = false) => {
      setNextLotsLoading(true);
      try {
        const response = await axios.get(
          `${url}/conductor/auction/${id}/list-next-lot`,
          {
            params: { page, page_size: 10 },
            headers: {
              Accept: "application/json",
              Authorization: `${tokenType} ${token}`,
            },
          },
        );

        if (!response?.data?.status) {
          console.warn("fetchNextLots: invalid response", response?.data);
          return;
        }

        const resData = response?.data?.data;
        // Filter: only items where source_lot is NOT "NotSell"
        const nextLotItems = (resData?.next_lot || []).filter(
          (lot) => !lot.is_cancel_for_auction && lot.source_lot !== "NotSell",
        );

        const currentPage = resData?.page != null ? resData.page : page;
        const totalPage =
          resData?.total_page != null ? resData.total_page : 1;

        nextLotsPageRef.current = currentPage;
        setNextLotsPage(currentPage);
        setNextLotsTotalPage(totalPage);

        setAuctionData((prev) => {
          const newNextLot = append
            ? [...prev.nextLot, ...nextLotItems]
            : nextLotItems;
          return {
            ...prev,
            nextLot: newNextLot,
          };
        });
      } catch (error) {
        console.error("Error fetching next lots:", error);
      } finally {
        setNextLotsLoading(false);
      }
    },
    [id, url, token, tokenType],
  );

  /**
   * Fetches not-sold lots from API using source_lot=NotSell param
   */
  const fetchNotSell = useCallback(
    async (page = 1, append = false) => {
      setNotSellLoading(true);
      try {
        const response = await axios.get(
          `${url}/conductor/auction/${id}/list-next-lot`,
          {
            params: { page, page_size: 60, source_lot: "NotSell" },
            headers: {
              Accept: "application/json",
              Authorization: `${tokenType} ${token}`,
            },
          },
        );

        if (!response?.data?.status) {
          console.warn("fetchNotSell: invalid response", response?.data);
          return;
        }

        const resData = response?.data?.data;
        const notSellItems = (resData?.next_lot || [])
          .filter((lot) => !lot.is_cancel_for_auction)
          .map((el) => ({ ...el, unit_auction_status: "NOT_SOLD" }));

        const currentPage = resData?.page != null ? resData.page : page;
        const totalPage = resData?.total_page != null ? resData.total_page : 1;

        setNotSellPage(currentPage);
        setNotSellTotalPage(totalPage);

        setAuctionData((prev) => ({
          ...prev,
          notSell: append ? [...prev.notSell, ...notSellItems] : notSellItems,
        }));
      } catch (error) {
        console.error("Error fetching not sell lots:", error);
      } finally {
        setNotSellLoading(false);
      }
    },
    [id, url, token, tokenType],
  );

  // Memoized computed values

  // Derived list: nextLot + notSell combined for carousel display
  const listLot = useMemo(
    () => [...auctionData.nextLot, ...auctionData.notSell],
    [auctionData.nextLot, auctionData.notSell],
  );

  /**
   * Memoized current lot information for performance optimization
   * @type {Object}
   */
  const currentLotInfo = useMemo(() => {
    // Handle null currentUnit case
    if (!auctionData.currentUnit) {
      return {
        lotNumber: "-",
        unitName: "Tidak ada unit yang sedang dilelang",
        basePrice: "0",
        multipleBid: auctionData.multipleBid || "0",
        tags: [],
        pictures: [],
        number_of_nipl: 1,
      };
    }

    return {
      lotNumber: auctionData.currentUnit?.lot_number ?? "-",
      unitName: auctionData.currentUnit?.unit_name ?? "-",
      basePrice: auctionData.currentUnit?.base_price ?? "0",
      multipleBid: auctionData.multipleBid || "0",
      tags: auctionData.currentUnit?.tags || [],
      pictures: auctionData.currentUnit?.pictures || [],
      number_of_nipl: auctionData.currentUnit?.number_of_nipl || [],
    };
  }, [auctionData.currentUnit, auctionData.multipleBid]);

  /**
   * Memoized auction information for performance optimization
   * @type {Object}
   */
  const auctionInfo = useMemo(
    () => ({
      name: auctionData.auction?.name ?? "",
      locationName:
        auctionData.auction?.location?.name ??
        auctionData.auction?.location_name ??
        "",
      dateStart: auctionData.auction?.date_start ?? new Date(),
      timeStart: auctionData.auction?.time_start ?? "",
      timeFinish: auctionData.auction?.time_finish ?? "",
      laneName: auctionData.auction?.auction_lane_name ?? "",
      status: auctionData.status,
    }),
    [auctionData.auction, auctionData.status],
  );

  const {
    status: socketStatus,
    failed: socketFailed,
    reconnect: reconnectSocket,
  } = useSocketConnection(
    socket,
    auctionInfo.status !== AUCTION_STATUS.CLOSED,
  );

  /**
   * Memoized countdown display formatting for performance optimization
   * @type {Object}
   */
  const countdownDisplay = useMemo(() => {
    const { minutes, seconds } = auctionData.countDown;
    return {
      minutes:
        minutes.toString().length > 1
          ? minutes.toString()
          : "0" + minutes.toString(),
      seconds:
        seconds.toString().length > 1
          ? seconds.toString()
          : "0" + seconds.toString(),
    };
  }, [auctionData.countDown]);

  /**
   * Memoized countdown color classes based on remaining time
   * @type {Object}
   */
  const countdownColorClasses = useMemo(() => {
    const { minutes, seconds } = auctionData.countDown;
    const totalSeconds = minutes * 60 + seconds;

    if (totalSeconds > 10) {
      // Putih - diatas 10 detik
      return {
        textColor: "text-gray-700",
      };
    } else if (totalSeconds > 3) {
      // Kuning - diatas 3 dan dibawah atau sama dengan 10 detik
      return {
        textColor: "text-yellow-500",
      };
    } else {
      // Merah - 3 detik kebawah
      return {
        textColor: "text-red-500",
      };
    }
  }, [auctionData.countDown]);

  // =================================================================

  // Auto-close modal effect
  useEffect(() => {
    let timer;
    if (
      modals.main.show &&
      (modals.main.body === "Unit berhasil terjual." ||
        modals.main.body === "Unit tidak terjual.")
    ) {
      timer = setTimeout(() => {
        setModals((prev) => ({
          ...prev,
          main: { ...prev.main, show: false },
        }));
      }, 5000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [modals.main.show, modals.main.body]);

  // Reset idLotManual when manual lot selection modal appears
  useEffect(() => {
    if (
      auctionInfo.status === AUCTION_STATUS.PENDING &&
      auctionData.nextLot?.length === 0 &&
      auctionData?.notSell?.length > 0 &&
      auctionData?.currentUnit === null
    ) {
      console.log(
        "🔄 Resetting idLotManual - manual lot selection modal appeared",
      );
      setUiState((prev) => ({
        ...prev,
        idLotManual: "",
      }));
    }
  }, [
    auctionInfo.status,
    auctionData.nextLot?.length,
    auctionData.notSell?.length,
    auctionData.currentUnit,
  ]);

  useEffect(() => {
    console.log("🟡 Connecting server...");
    socket.on("connect", () => {
      console.log("✅ Connected to server");
      auctionActions.join();
    });

    // Socket event handlers
    const socketEvents = [
      "JoinAuctionResponse",
      "StartAuctionResponse",
      "SendBidResponse",
      "PendingResponse",
      "SetAutopilotResponse",
      "AutopilotResponse",
      "ReSellResponse",
      "NextLotResponse",
      "FloorResponse",
    ];

    socketEvents.forEach((event) => {
      socket.on(event, (response) => {
        console.log(`📩 ${event}:`, response);

        // Get current unit ID from response (current_unit is an array)
        const currentUnitArray = response?.data?.current_unit || [];
        const currentUnitId =
          currentUnitArray.length > 0 ? currentUnitArray[0]?.id : null;

        // Refresh next lots from API on events that change the lot list
        if (
          event === "JoinAuctionResponse" ||
          event === "StartAuctionResponse" ||
          event === "PendingResponse" ||
          event === "NextLotResponse" ||
          event === "ReSellResponse"
        ) {
          fetchNextLots(1, false);
          currentNextLotSlideRef.current = 0;
          setUiState((prev) => ({ ...prev, curIndex: 0 }));
        }

        if (event === "StartAuctionResponse") {
          setUiState((prev) => ({ ...prev, startLoading: false }));
        }

        // Fetch not-sell lots ONLY when the manual lot selection modal should appear:
        // status PENDING, next_lot is empty, and there are not_sell lots available
        if (event === "JoinAuctionResponse" || event === "PendingResponse" || event === "NextLotResponse") {
          const pendingStatus = response?.data?.auction?.status === AUCTION_STATUS.PENDING;
          const noNextLot = (response?.data?.next_lot || []).filter(
            (lot) => !lot.is_cancel_for_auction
          ).length === 0;
          const hasNotSell = (response?.data?.not_sell || []).length > 0;
          const noCurrentUnit = !response?.data?.current_unit?.length;

          if (pendingStatus && noNextLot && hasNotSell && noCurrentUnit) {
            fetchNotSell();
          }

          // Autopilot: lot habis (tidak ada next_lot dan tidak ada current_unit) → fetch not sell
          const isAutopilot = response?.data?.auction?.is_autopilot === 1;
          if (event === "NextLotResponse" && isAutopilot && noNextLot && noCurrentUnit) {
            console.log("🤖 Autopilot: lot habis - fetching not sell");
            fetchNotSell(1, false);
          }
        }

        // Derive lastLot from API-fetched listLot after refresh
        // For immediate feedback, use socket data as a hint
        const nextLotArray = response?.data?.next_lot || [];
        const notSellArray = response?.data?.not_sell || [];
        const hasRemainingLots =
          nextLotArray.length > 0 || notSellArray.length > 0;
        const isLastLot = currentUnitId && !hasRemainingLots;

        if (event === "JoinAuctionResponse" || event === "PendingResponse") {
          setAuctionData((prev) => ({ ...prev, lastLot: isLastLot }));
        }

        if (event === "NextLotResponse") {
          // Check if the NEW current lot (from response) is the last lot
          setAuctionData((prev) => ({ ...prev, lastLot: isLastLot }));
        }

        handleSocketAuction(response.data, event, response?.extra);
      });
    });

    // Backend still has bidder(s) on record for a unit the operator marked
    // "Tidak Terjual" -- nothing was written server-side. Show what the
    // server actually knows and let the operator explicitly confirm the
    // override, instead of the (possibly stale) local bidder list deciding
    // silently. See ForceNotSold/ConfirmOverride in be/request/auction.go.
    socket.on("NextLotConfirmationRequired", (response) => {
      console.log("⚠️ NextLotConfirmationRequired:", response);
      const detail = response?.data || {};
      setModals((prev) => ({
        ...prev,
        main: {
          show: true,
          title: "Konfirmasi Tidak Terjual",
          body: `Server masih mencatat ${detail.bidder_count ?? 0} bid untuk unit ini (tertinggi ${formatRupiah(detail.highest_offer ?? 0)} dari ${detail.bidder_email ?? "-"}). Unit akan ditandai Tidak Terjual dan bid ini diabaikan. Lanjutkan?`,
          buttonText: "Tetap Tandai Tidak Terjual",
          cancelText: true,
          onSubmit: () => {
            setModals((prev) => ({
              ...prev,
              main: { ...prev.main, show: false },
            }));
            emitSocketEvent("NextLot", {
              room: AUCTION_SOCKET_ROOM,
              winner_id: 0,
              force_not_sold: true,
              confirm_override: true,
            });
          },
        },
      }));
    });

    socket.on("error", (err) => {
      console.log("❌ error:", err);
      // socket.io-client v2 meneruskan error transport (mis. "websocket error"
      // saat koneksi putus) ke event ini, bukan hanya error dari server.
      // Socket reconnect sendiri dan statusnya tampil di SocketStatusBadge,
      // jadi tidak perlu modal.
      if (err instanceof Error && err.type === "TransportError") {
        return;
      }
      if (err?.status === 401) {
        history.push("/login");
        window.location.reload();
      } else {
        clearAutopilotTimeout();
        setUiState((prev) => ({ ...prev, loading: false }));
        showError(err?.message);
      }
    });

    // Bukan modal: connect_error juga muncul di sela percobaan reconnect yang
    // akhirnya berhasil (mis. tab lama tidak aktif). Status koneksi ditampilkan
    // SocketStatusBadge; modal muncul hanya bila lama tidak tersambung.
    socket.on("connect_error", (err) => {
      console.log("❌ Connection error:", err.message);
      setUiState((prev) => ({ ...prev, startLoading: false }));
    });

    return () => {
      console.log("🛑 Disconnecting socket...");
      socket.disconnect();
    };
  }, [id, fetchNextLots, fetchNotSell]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    window.addEventListener("focus", auctionActions.join);
    return () => {
      window.removeEventListener("focus", auctionActions.join);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Countdown effect
  // Autoload next lots when carousel approaches the end
  useEffect(() => {
    const visibleCount = window.innerWidth >= 640 ? 3 : 2;
    const nearEnd = uiState.curIndex + visibleCount >= listLot.length - 1;
    if (nearEnd && nextLotsPage < nextLotsTotalPage && !nextLotsLoading) {
      fetchNextLots(nextLotsPage + 1, true);
    }
  }, [uiState.curIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (auctionData.countDown.run) {
      if (auctionData.auction?.current_stock_unit?.number_of_nipl === 1) {
        setUiState((prev) => ({ ...prev, buttonDisabled: false }));
      }

      intervalCountDown.current = setInterval(() => {
        setAuctionData((prev) => {
          const { minutes, seconds } = prev.countDown;
          if (minutes === 0 && seconds === 0) {
            clearInterval(intervalCountDown.current);
            return { ...prev, countDown: { ...prev.countDown, run: false } };
          }
          if (minutes === 0 && seconds <= 3) {
            setUiState((prev) => ({ ...prev, buttonDisabled: true }));
          }
          if (seconds > 0) {
            return {
              ...prev,
              countDown: { ...prev.countDown, seconds: seconds - 1 },
            };
          } else {
            return {
              ...prev,
              countDown: {
                ...prev.countDown,
                minutes: minutes - 1,
                seconds: 59,
              },
            };
          }
        });
      }, 1000);
    } else {
      if (intervalCountDown.current) clearInterval(intervalCountDown.current);
    }
    return () => {
      if (intervalCountDown.current) clearInterval(intervalCountDown.current);
    };
  }, [auctionData.countDown.run, auctionData.auction]);

  // Load data on mount
  useEffect(() => {
    localStorage.removeItem(`bidder-${id}`);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Loading Skeleton Component
  const LoadingSkeleton = () => (
    <div className="pb-8 animate-pulse">
      {/* Header Skeleton */}
      <div className="flex px-4 md:px-24 my-6 gap-4">
        <div className="w-2/3 md:w-4/5 bg-gray-200 h-16 rounded-xl"></div>
        <div className="w-1/3 md:w-1/3 flex gap-4">
          <div className="w-1/3 bg-gray-200 h-16 rounded-xl"></div>
          <div className="w-1/3 bg-gray-200 h-16 rounded-xl"></div>
          <div className="w-1/3 bg-gray-200 h-16 rounded-xl"></div>
        </div>
      </div>

      {/* Main Content Skeleton */}
      <div className="grid grid-cols-12 mx-4 gap-6 md:mx-24">
        {/* Left Column */}
        <div className="grid grid-rows-2 gap-y-6 col-span-12 lg:col-span-6">
          {/* Current Unit Skeleton */}
          <div className="grid grid-cols-12 col-span-12 p-6 bg-gray-200 gap-x-6 rounded-xl h-[40vh]">
            <div className="col-span-7 bg-gray-300 rounded-xl"></div>
            <div className="col-span-5 space-y-4">
              <div className="h-8 bg-gray-300 rounded w-3/4"></div>
              <div className="h-6 bg-gray-300 rounded w-full"></div>
              <div className="h-6 bg-gray-300 rounded w-1/2"></div>
              <div className="h-8 bg-gray-300 rounded w-2/3"></div>
            </div>
          </div>

          {/* Lot List Skeleton */}
          <div className="grid grid-cols-12 col-span-12 gap-x-2">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="col-span-6 md:col-span-4 bg-gray-200 rounded-xl h-64"
              ></div>
            ))}
          </div>
        </div>

        {/* Right Column */}
        <div className="grid grid-rows-2 gap-y-6 col-span-12 lg:col-span-6">
          {/* Proxy Bid & Bidding Skeleton */}
          <div className="grid grid-cols-12 gap-x-5">
            <div className="col-span-6 bg-gray-200 rounded-xl h-[350px]"></div>
            <div className="col-span-6 bg-gray-200 rounded-xl h-[350px]"></div>
          </div>

          {/* Countdown & Actions Skeleton */}
          <div className="grid grid-rows-5 gap-y-5">
            <div className="bg-gray-200 rounded-xl h-16"></div>
            <div className="row-span-4 bg-gray-200 rounded-xl"></div>
          </div>
        </div>
      </div>
    </div>
  );

  if (uiState.initialLoading) {
    return <LoadingSkeleton />;
  }

  return (
    <div className="pb-8" role="main" aria-label="Live Bidding Auction">
      {auctionInfo.status !== AUCTION_STATUS.CLOSED ? (
        <SocketStatusBadge status={socketStatus} />
      ) : null}
      <Modal
        show={socketFailed}
        closeModal={() => {}}
        title="Koneksi Terputus"
        body="Koneksi ke server lelang terputus lebih dari 1 menit. Sistem terus mencoba menyambung ulang; klik tombol di bawah untuk mencoba sekarang."
        textSubmit="Sambungkan Ulang"
        onSubmit={reconnectSocket}
      />
      <DotLoaderOverlay
        loading={uiState.loading}
        overlayColor={COLORS.OVERLAY.DARK}
        color={COLORS.BRAND.PRIMARY}
        size={30}
      />
      <Modal
        show={auctionInfo.status === AUCTION_STATUS.PENDING}
        noFooter={true}
        closeModal={() => {}}
        body={
          <div className="w-[350px] flex items-center flex-col py-4">
            <img
              className="w-[80px] h-[80px] mb-6"
              alt="pause"
              src={require("../../assets/images/circle-pause.gif")}
            />
            <div className="font-bold text-2xl mb-3 text-third">
              Lelang Dijeda
            </div>
            {auctionInfo.status === AUCTION_STATUS.PENDING &&
            auctionData.nextLot?.length === 0 &&
            auctionData?.notSell?.length > 0 &&
            auctionData?.currentUnit === null ? (
              <>
                <div className="text-text-muted text-sm text-center mb-4 px-4">
                  Pilih Lot dan klik tombol lanjutkan lelang untuk melanjutkan
                  lelang
                </div>
                <div className="relative px-6 flex-auto w-[100%] text-left mb-4">
                  <div className="mb-3 text-sm font-semibold text-third">
                    Pilih Lot Manual <span className="text-primary">*</span>
                  </div>
                  <Select
                    key={`lot-select-${uiState.idLotManual || "empty"}`}
                    options={auctionData.notSell?.map((data) => ({
                      value: data.id,
                      label: `Lot - ${auctionInfo.laneName}${data?.lot_number}`,
                    }))}
                    value={
                      uiState.idLotManual
                        ? auctionData.notSell
                            ?.map((data) => ({
                              value: data.id,
                              label: `Lot - ${auctionInfo.laneName}${data?.lot_number}`,
                            }))
                            .find((opt) => opt.value === uiState.idLotManual)
                        : null
                    }
                    onChange={onChangeLot}
                    placeholder="-Pilih Lot Manual Selanjutnya-"
                    isClearable
                    isSearchable
                    isLoading={notSellLoading}
                    onMenuScrollToBottom={() => {
                      if (notSellPage < notSellTotalPage && !notSellLoading) {
                        fetchNotSell(notSellPage + 1, true);
                      }
                    }}
                    aria-label="Pilih Lot Manual Selanjutnya"
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        backgroundColor: "#f9fafb",
                        borderColor: state.isFocused
                          ? COLORS.BRAND.PRIMARY
                          : "#d1d5db",
                        borderRadius: "0.5rem",
                        padding: "0.375rem",
                        boxShadow: state.isFocused
                          ? `0 0 0 1px ${COLORS.BRAND.PRIMARY}`
                          : "none",
                        "&:hover": {
                          borderColor: COLORS.BRAND.PRIMARY,
                        },
                        transition: "all 0.2s",
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected
                          ? COLORS.BRAND.PRIMARY
                          : state.isFocused
                            ? "#e0f2fe"
                            : "white",
                        color: state.isSelected ? "white" : "#111827",
                        cursor: "pointer",
                        "&:active": {
                          backgroundColor: COLORS.BRAND.PRIMARY,
                        },
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: "#6b7280",
                        fontSize: "0.875rem",
                      }),
                      singleValue: (base) => ({
                        ...base,
                        color: "#111827",
                        fontSize: "0.875rem",
                      }),
                      menu: (base) => ({
                        ...base,
                        borderRadius: "0.5rem",
                        boxShadow:
                          "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
                        overflow: "hidden",
                      }),
                      menuList: (base) => ({
                        ...base,
                        padding: "0.25rem",
                      }),
                    }}
                  />
                </div>
                <Button
                  className={`w-[80%] py-3 mb-3 ${
                    !uiState.idLotManual ? "bg-gray-400 cursor-not-allowed" : ""
                  }`}
                  title={
                    <div className="flex items-center justify-center">
                      <Play className="w-6 h-6" />
                      <span className="ml-2 font-semibold">
                        Lanjutkan Kembali
                      </span>
                    </div>
                  }
                  onClick={() => manageAuction("start", uiState.idLotManual)}
                  disabled={!uiState.idLotManual}
                  aria-label="Lanjutkan lelang dengan lot yang dipilih"
                />
                <div className="text-text-muted text-sm my-3">atau</div>
                <Button
                  className="w-[80%] py-3 mt-2"
                  title={
                    <div className="flex items-center justify-center">
                      <Stop className="w-6 h-6" />
                      <span className="ml-2 font-semibold">
                        Hentikan Lelang
                      </span>
                    </div>
                  }
                  onClick={() =>
                    showConfirmation(
                      "Apa Anda yakin menghentikan lelang sekarang?",
                      () => {
                        manageAuction("finish");
                      },
                    )
                  }
                  aria-label="Hentikan lelang secara permanen"
                />
              </>
            ) : auctionInfo.status === AUCTION_STATUS.PENDING &&
              auctionData.nextLot?.length === 0 &&
              auctionData?.notSell?.length === 0 &&
              auctionData?.currentUnit === null ? (
              <>
                <div className="text-text-muted text-sm text-center mb-6 px-4">
                  Tidak ada lot yang tersedia untuk dilelang
                </div>
                <Button
                  className="w-[80%] py-3 mt-2"
                  title={
                    <div className="flex items-center justify-center">
                      <Stop className="w-5 h-5 text-white" />
                      <span className="ml-2 font-semibold">
                        Hentikan Lelang
                      </span>
                    </div>
                  }
                  onClick={() =>
                    showConfirmation(
                      "Apa Anda yakin menghentikan lelang sekarang?",
                      () => {
                        manageAuction("finish");
                      },
                    )
                  }
                  aria-label="Hentikan lelang karena tidak ada lot tersisa"
                />
              </>
            ) : (
              <>
                <div className="text-text-muted text-sm text-center mb-6 px-4">
                  Klik tombol di bawah untuk melanjutkan lelang
                </div>
                <Button
                  className="w-[80%] py-3 mt-2"
                  title={
                    <div className="flex items-center justify-center">
                      <Play className="w-5 h-5" />
                      <span className="ml-2 font-semibold">
                        Lanjutkan Kembali
                      </span>
                    </div>
                  }
                  onClick={() => manageAuction("pending", true)}
                  aria-label="Lanjutkan lelang dari status pause"
                />
              </>
            )}
          </div>
        }
      />
      <ModalPilihPemenang
        show={modals.pemenang.show}
        onSubmit={modals.pemenang.onSubmit}
        body={modals.pemenang.body}
      />
      <ModalHentikanLelang
        show={modals.hentikan.show}
        onSubmit={modals.hentikan.onSubmit}
        type={modals.hentikan.type}
        body={modals.hentikan.body}
      />
      <Modal
        show={modals.main.show}
        closeModal={() =>
          setModals((prev) => ({
            ...prev,
            main: { ...prev.main, show: false },
          }))
        }
        title={modals.main.title}
        body={modals.main.body}
        onSubmit={modals.main.onSubmit}
        textSubmit={modals.main.buttonText}
        textCancel={modals.main.cancelText ? "Batal" : null}
      />
      <div className="flex px-4 md:px-24 my-6 gap-4">
        <div
          className={`md:flex md:justify-between bg-white ${
            auctionInfo.status === AUCTION_STATUS.COMING
              ? "w-[100%]"
              : "w-2/3 md:w-4/5"
          } py-4 px-5 md:px-6 rounded-xl items-center card shadow-md border border-md`}
        >
          <div className="flex flex-col">
            {auctionInfo.name ? (
              <span className="text-third font-bold text-lg">
                {auctionInfo.name}
              </span>
            ) : null}
            <span className="text-third font-semibold text-base">
              {auctionInfo.locationName
                ? `${auctionInfo.locationName} | `
                : ""}
              {formatDate(auctionInfo.dateStart, 3)}
            </span>
          </div>
          <div className="flex items-center md:mt-0 mt-3">
            <Clock className="mr-2 w-5 md:w-6" />
            <span className="text-third text-sm font-medium">
              {auctionInfo.timeStart.substr(0, 5)} -{" "}
              {auctionInfo.timeFinish.substr(0, 5)} WIB
            </span>
          </div>
        </div>
        <div className="flex md:justify-between w-1/3 md:w-1/3 gap-4">
          <Button
            bgColor={
              auctionData.bidder.length > 0 || uiState.disabledHentikan
                ? "bg-gray-300"
                : "bg-white"
            }
            className={`w-1/3 px-2 md:px-4 rounded-xl shadow-md transition-colors ${
              auctionData.bidder.length > 0 || uiState.disabledHentikan
                ? "border border-gray-300 cursor-not-allowed"
                : "border border-gray-200 hover:border-gray-300"
            }`}
            title={
              <div className="flex items-center justify-center">
                <Stop
                  className={`w-5 h-5 md:w-6 md:h-6 ${
                    auctionData.bidder.length > 0 || uiState.disabledHentikan
                      ? "text-gray-500"
                      : "text-black"
                  }`}
                />
                <span
                  className={`ml-2 text-sm font-semibold ${
                    auctionData.bidder.length > 0 || uiState.disabledHentikan
                      ? "text-gray-500"
                      : "text-black"
                  }`}
                >
                  Hentikan
                </span>
              </div>
            }
            disabled={auctionData.bidder.length > 0 || uiState.disabledHentikan}
            onClick={() =>
              showConfirmation(
                "Apa Anda yakin menghentikan lelang sekarang?",
                () => {
                  if (auctionData.bidder.length === 0) {
                    manageAuction("finish");
                  }
                },
              )
            }
            aria-label="Hentikan lelang"
            aria-describedby="stop-auction-description"
          />

          <Button
            bgColor={
              auctionInfo.status === AUCTION_STATUS.COMING ||
              (auctionInfo.status !== AUCTION_STATUS.STARTED &&
                auctionInfo.status !== AUCTION_STATUS.PENDING)
                ? "bg-gray-300"
                : "bg-bg-pause"
            }
            className={`w-1/3 px-2 md:px-4 rounded-xl shadow-md transition-colors ${
              auctionInfo.status === AUCTION_STATUS.COMING ||
              (auctionInfo.status !== AUCTION_STATUS.STARTED &&
                auctionInfo.status !== AUCTION_STATUS.PENDING)
                ? "cursor-not-allowed"
                : ""
            }`}
            disabled={
              auctionInfo.status === AUCTION_STATUS.COMING ||
              (auctionInfo.status !== AUCTION_STATUS.STARTED &&
                auctionInfo.status !== AUCTION_STATUS.PENDING)
            }
            title={
              <div className="flex items-center justify-center">
                <Pause
                  className={`w-5 h-5 md:w-6 md:h-6 ${
                    auctionInfo.status === AUCTION_STATUS.COMING ||
                    (auctionInfo.status !== AUCTION_STATUS.STARTED &&
                      auctionInfo.status !== AUCTION_STATUS.PENDING)
                      ? "text-gray-500"
                      : "text-red-500"
                  }`}
                />
                <span
                  className={`ml-2 text-sm font-semibold ${
                    auctionInfo.status === AUCTION_STATUS.COMING ||
                    (auctionInfo.status !== AUCTION_STATUS.STARTED &&
                      auctionInfo.status !== AUCTION_STATUS.PENDING)
                      ? "text-gray-500"
                      : "text-primary"
                  }`}
                >
                  Jeda
                </span>
              </div>
            }
            onClick={() =>
              showConfirmation(
                "Apa Anda yakin menjeda lelang sekarang?",
                () => {
                  manageAuction("pending");
                },
              )
            }
            aria-label="Jeda lelang sementara"
            aria-describedby="pause-auction-description"
          />

          <div className="w-1/3 px-0">
            <ToggleSwitch
              label={"Autopilot"}
              onChange={toggleAutopilot}
              value={uiState.isAutopilot}
              aria-label="Toggle autopilot mode"
            />
          </div>
        </div>
      </div>

      {auctionInfo.status === AUCTION_STATUS.CLOSED ? (
        <div className="grid grid-cols-12 mx-4 gap-6 md:mx-24">
          <div
            className="grid h-[36rem] bg-gradient-to-br from-primary to-red-700 card col-span-12 rounded-2xl items-center justify-center shadow-lg"
            role="status"
            aria-live="polite"
          >
            <label className="text-white text-4xl font-bold">
              Lelang Telah Selesai
            </label>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-12 mx-4 gap-6 md:mx-24">
          <div className="grid grid-rows-2 gap-y-6 col-span-12 lg:col-span-6">
            {!auctionData.currentUnit ? (
              <div className="col-span-12 flex flex-col items-center justify-center border border-grey py-16 bg-white rounded-xl shadow-md">
                <div className="col-span-12 flex flex-col items-center justify-center w-[100%]">
                  <img
                    alt="waiting"
                    className="w-[50px] h-[90px] opacity-70"
                    src={require("../../assets/images/sand-timer.png")}
                  />
                  <div className="text-center mt-10 px-6">
                    <h3 className="text-2xl font-bold text-third mb-4">
                      Tidak Ada Unit Aktif
                    </h3>
                    <p className="text-text-muted text-sm leading-relaxed">
                      Saat ini tidak ada unit yang sedang dilelang. Silakan
                      pilih unit dari daftar lot di bawah untuk memulai lelang.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-12 col-span-12 p-6 bg-white card gap-x-6 rounded-xl shadow-md border border-grey">
                <div className="col-span-7">
                  {!!currentLotInfo.pictures &&
                  currentLotInfo.pictures.length > 0 ? (
                    <img
                      src={currentLotInfo.pictures[0]?.image_url}
                      className="rounded-xl h-[35vh] w-[100%] object-cover object-center shadow-md"
                      onError={({ currentTarget }) => {
                        currentTarget.onerror = null;
                        currentTarget.src = require("../../assets/images/no-image.jpg");
                      }}
                      alt={`Gambar unit ${currentLotInfo.unitName}`}
                    />
                  ) : (
                    <img
                      src={require("../../assets/images/no-image.jpg")}
                      className="rounded-xl h-[35vh] w-[100%] object-cover object-center shadow-md"
                      alt={`Gambar unit ${currentLotInfo.unitName}`}
                    />
                  )}
                </div>
                <div className="col-span-5">
                  <div className="mb-4 flex gap-2">
                    <label className="py-2 px-3 bg-primary text-white rounded-lg text-md font-semibold shadow-sm">
                      Lot-{auctionInfo.laneName}
                      {currentLotInfo.lotNumber}
                    </label>
                    {currentLotInfo.number_of_nipl > 1 && (
                      <label className="py-2 px-3 bg-special-nipl text-white rounded-lg text-sm font-semibold flex items-center gap-1.5 shadow-sm">
                        <SpecialNiplIcon style={{ width: 14 }} />
                        Special NIPL
                      </label>
                    )}
                  </div>
                  <div className="mb-6">
                    <label className="text-third text-lg font-bold leading-tight">
                      {currentLotInfo.unitName}
                    </label>
                  </div>
                  <div className="mb-2">
                    <label className="text-secondary text-sm font-medium">
                      Harga Dasar
                    </label>
                  </div>
                  <div className="mb-5">
                    <label className="text-third text-xl font-bold">
                      {formatRupiah(currentLotInfo.basePrice, ".")}
                    </label>
                  </div>
                  <div className="mb-2">
                    <label className="text-secondary text-sm font-medium">
                      Kelipatan Bid
                    </label>
                  </div>
                  <div className="mb-5">
                    <label className="font-bold text-third text-xl">
                      {formatRupiah(currentLotInfo.multipleBid, ".")}
                    </label>
                  </div>
                  <div className="mb-2">
                    <label className="text-secondary text-sm font-medium">
                      Highlight
                    </label>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {currentLotInfo.tags.map((tag) => (
                      <div
                        key={`tag-${tag.id}`}
                        className="bg-bg-tag px-3 py-1.5 text-secondary text-sm font-semibold rounded-lg"
                      >
                        {tag.name}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            <div className="grid grid-cols-12 col-span-12 gap-x-2 relative">
              {listLot.length === 0 ? (
                <div className="col-span-12 flex flex-col items-center justify-center border border-grey py-16 bg-white rounded-xl shadow-md">
                  <div className="text-center px-8">
                    <h3 className="text-xl font-bold text-third mb-3">
                      {nextLotsLoading ? "Memuat lot..." : "Tidak Ada Lot Tersedia"}
                    </h3>
                    <p className="text-text-muted text-sm leading-relaxed mb-4">
                      {nextLotsLoading
                        ? ""
                        : "Semua lot telah selesai dilelang atau belum ada lot yang ditambahkan."}
                    </p>
                  </div>
                </div>
              ) : (
                listLot.map((lot, idx) => (
                  <div
                    className={`
                    relative col-span-6 md:col-span-4 card max-h-120 border-2 rounded-xl shadow-sm transition-all ${
                      lot?.id === auctionData.currentUnit?.id &&
                      "border-primary shadow-lg ring-2 ring-primary ring-opacity-50"
                    }
                    ${
                      lot?.unit_auction_status === "NOT_SOLD" &&
                      auctionData.status === "PENDING" &&
                      "cursor-pointer hover:shadow-lg hover:border-primary"
                    }
                    hidden ${
                      idx >= uiState.curIndex &&
                      idx <= uiState.curIndex + 1 &&
                      "max-sm:block"
                    }
                    hidden ${
                      idx >= uiState.curIndex &&
                      idx <= uiState.curIndex + 2 &&
                      "sm:block"
                    }
                  `}
                    key={`lot-${lot.id}-${idx}`}
                    onClick={() => {
                      if (lot?.unit_auction_status === "NOT_SOLD") {
                        showConfirmation(
                          `Apa Anda yakin mulai lelang manual lot ini?`,
                          () => {
                            manageAuction("start", lot?.id);
                          },
                        );
                      }
                    }}
                    role={
                      lot?.unit_auction_status === "NOT_SOLD" &&
                      auctionData.status === "PENDING"
                        ? "button"
                        : undefined
                    }
                    tabIndex={
                      lot?.unit_auction_status === "NOT_SOLD" &&
                      auctionData.status === "PENDING"
                        ? 0
                        : undefined
                    }
                    aria-label={
                      lot?.unit_auction_status === "NOT_SOLD" &&
                      auctionData.status === "PENDING"
                        ? `Mulai lelang manual untuk lot ${lot?.lot_number}`
                        : `Lot ${lot?.lot_number} - ${lot?.unit_name}`
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        if (lot?.unit_auction_status === "NOT_SOLD") {
                          showConfirmation(
                            `Apa Anda yakin mulai lelang manual lot ini?`,
                            () => {
                              manageAuction("start", lot?.id);
                            },
                          );
                        }
                      }
                    }}
                  >
                    <div className="h-36 xl:h-48 w-[100%]">
                      <img
                        className="rounded-t-xl h-36 xl:h-48 w-[100%] object-cover object-center"
                        src={
                          lot?.pictures?.[0]?.image_url ||
                          require("../../assets/images/no-image.jpg")
                        }
                        alt="Car"
                        onError={({ currentTarget }) => {
                          currentTarget.onerror = null;
                          currentTarget.src = require("../../assets/images/no-image.jpg");
                        }}
                      />
                    </div>
                    {lot.unit_inspection?.grade && (
                      <div className="absolute top-3 right-3 bg-white px-3 py-1.5 rounded-lg text-primary font-semibold card shadow-md">
                        Grade {lot.unit_inspection?.grade || "-"}
                      </div>
                    )}
                    <div
                      className="bg-lot-pattern bg-contain bg-no-repeat px-1 text-white font-semibold absolute"
                      style={{
                        width: "41%",
                        fontSize: "small",
                        position:
                          lot?.number_of_nipl > 1 ? "absolute" : "static",
                      }}
                    >
                      Lot - {auctionInfo.laneName}
                      {lot?.lot_number}
                    </div>

                    {lot?.number_of_nipl > 1 && (
                      <div
                        className="bg-special-nipl-pattern bg-contain bg-no-repeat px-1 text-white font-semibold"
                        style={{ fontSize: "small" }}
                      >
                        <span style={{ marginLeft: "30%" }}>Special NIPL</span>
                      </div>
                    )}

                    <div className="mx-3 mt-3 h-16 max-h-10 text-sm font-semibold text-secondary box-border leading-tight">
                      {lot?.unit_name}
                    </div>
                    <label className="block mt-4 mx-3 font-medium text-xs text-secondary">
                      Harga Dasar
                    </label>
                    <label className="block mx-3 mb-4 text-lg font-bold text-third">
                      {formatRupiah(lot?.base_price ?? "0", ".")}
                    </label>
                  </div>
                ))
              )}
              {listLot.length > 2 && (
                <>
                  <MediaQuery maxWidth={639}>
                    <div
                      className={`bg-white border-primary border-2 rounded-full w-10 h-10 absolute -left-5 top-[50%] -translate-y-1/2 flex items-center justify-center shadow-lg transition-all z-10 ${
                        uiState.curIndex === 0
                          ? "bg-gray-200 cursor-not-allowed opacity-50"
                          : "hover:shadow-xl hover:bg-primary hover:scale-110 cursor-pointer"
                      }`}
                      onClick={
                        uiState.curIndex === 0
                          ? () => {}
                          : () =>
                              setUiState((prev) => ({
                                ...prev,
                                curIndex: prev.curIndex - 1,
                              }))
                      }
                      role="button"
                      tabIndex={0}
                      aria-label="Previous lot"
                      aria-disabled={uiState.curIndex === 0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          if (uiState.curIndex > 0) {
                            setUiState((prev) => ({
                              ...prev,
                              curIndex: prev.curIndex - 1,
                            }));
                          }
                        }
                      }}
                    >
                      <ChevronLeft
                        className="w-4 h-4"
                        color={
                          uiState.curIndex === 0 ? "#9CA3AF" : COLORS.TEXT.DARK
                        }
                      />
                    </div>
                    <div
                      className={`bg-white border-primary border-2 rounded-full w-10 h-10 absolute -right-5 top-[50%] -translate-y-1/2 flex items-center justify-center shadow-lg transition-all z-10 ${
                        uiState.curIndex + 2 >= listLot.length && nextLotsPage >= nextLotsTotalPage
                          ? "bg-gray-200 cursor-not-allowed opacity-50"
                          : "hover:shadow-xl hover:bg-primary hover:scale-110 cursor-pointer"
                      }`}
                      onClick={() => {
                        if (uiState.curIndex + 2 < listLot.length) {
                          setUiState((prev) => ({ ...prev, curIndex: prev.curIndex + 1 }));
                        } else if (nextLotsPage < nextLotsTotalPage && !nextLotsLoading) {
                          fetchNextLots(nextLotsPage + 1, true);
                          setUiState((prev) => ({ ...prev, curIndex: prev.curIndex + 1 }));
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      aria-label="Next lot"
                      aria-disabled={uiState.curIndex + 2 >= listLot.length && nextLotsPage >= nextLotsTotalPage}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          if (uiState.curIndex + 2 < listLot.length) {
                            setUiState((prev) => ({ ...prev, curIndex: prev.curIndex + 1 }));
                          } else if (nextLotsPage < nextLotsTotalPage && !nextLotsLoading) {
                            fetchNextLots(nextLotsPage + 1, true);
                            setUiState((prev) => ({ ...prev, curIndex: prev.curIndex + 1 }));
                          }
                        }
                      }}
                    >
                      {nextLotsLoading && uiState.curIndex + 2 >= listLot.length ? (
                        <span className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <ChevronRight
                          className="w-4 h-4"
                          color={
                            uiState.curIndex + 2 >= listLot.length && nextLotsPage >= nextLotsTotalPage
                              ? "#9CA3AF"
                              : COLORS.TEXT.DARK
                          }
                        />
                      )}
                    </div>
                  </MediaQuery>
                  <MediaQuery minWidth={640}>
                    {(listLot.length > 3 || nextLotsPage < nextLotsTotalPage) && (
                      <>
                        <div
                          className={`bg-white border-primary border-2 rounded-full w-10 h-10 absolute -left-5 top-[50%] -translate-y-1/2 flex items-center justify-center shadow-lg transition-all z-10 ${
                            uiState.curIndex === 0
                              ? "bg-gray-200 cursor-not-allowed opacity-50"
                              : "hover:shadow-xl hover:bg-primary hover:scale-110 cursor-pointer"
                          }`}
                          onClick={
                            uiState.curIndex === 0
                              ? () => {}
                              : () =>
                                  setUiState((prev) => ({
                                    ...prev,
                                    curIndex: prev.curIndex - 1,
                                  }))
                          }
                          role="button"
                          tabIndex={0}
                          aria-label="Previous lot"
                          aria-disabled={uiState.curIndex === 0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              if (uiState.curIndex > 0) {
                                setUiState((prev) => ({
                                  ...prev,
                                  curIndex: prev.curIndex - 1,
                                }));
                              }
                            }
                          }}
                        >
                          <ChevronLeft
                            className="w-4 h-4"
                            color={
                              uiState.curIndex === 0
                                ? "#9CA3AF"
                                : COLORS.TEXT.DARK
                            }
                          />
                        </div>
                        <div
                          className={`bg-white border-primary border-2 rounded-full w-10 h-10 absolute -right-5 top-[50%] -translate-y-1/2 flex items-center justify-center shadow-lg transition-all z-10 ${
                            uiState.curIndex + 3 >= listLot.length && nextLotsPage >= nextLotsTotalPage
                              ? "bg-gray-200 cursor-not-allowed opacity-50"
                              : "hover:shadow-xl hover:bg-primary hover:scale-110 cursor-pointer"
                          }`}
                          onClick={() => {
                            if (uiState.curIndex + 3 < listLot.length) {
                              setUiState((prev) => ({ ...prev, curIndex: prev.curIndex + 1 }));
                            } else if (nextLotsPage < nextLotsTotalPage && !nextLotsLoading) {
                              fetchNextLots(nextLotsPage + 1, true);
                              setUiState((prev) => ({ ...prev, curIndex: prev.curIndex + 1 }));
                            }
                          }}
                          role="button"
                          tabIndex={0}
                          aria-label="Next lot"
                          aria-disabled={uiState.curIndex + 3 >= listLot.length && nextLotsPage >= nextLotsTotalPage}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              if (uiState.curIndex + 3 < listLot.length) {
                                setUiState((prev) => ({ ...prev, curIndex: prev.curIndex + 1 }));
                              } else if (nextLotsPage < nextLotsTotalPage && !nextLotsLoading) {
                                fetchNextLots(nextLotsPage + 1, true);
                                setUiState((prev) => ({ ...prev, curIndex: prev.curIndex + 1 }));
                              }
                            }
                          }}
                        >
                          {nextLotsLoading && uiState.curIndex + 3 >= listLot.length ? (
                            <span className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <ChevronRight
                              className="w-4 h-4"
                              color={
                                uiState.curIndex + 3 >= listLot.length && nextLotsPage >= nextLotsTotalPage
                                  ? "#9CA3AF"
                                  : COLORS.TEXT.DARK
                              }
                            />
                          )}
                        </div>
                      </>
                    )}
                  </MediaQuery>
                </>
              )}
            </div>
          </div>
          {auctionInfo.status === AUCTION_STATUS.COMING && (
            <div className="grid-rows-2 gap-y-6 col-span-12 lg:col-span-6 flex card items-center justify-center bg-white border border-grey flex-col py-16 rounded-xl shadow-md">
              <img
                alt="waiting"
                className="w-[50px] h-[90px] opacity-80"
                src={require("../../assets/images/sand-timer.png")}
              />
              <div className="font-bold text-2xl mb-4 mt-8 text-third">
                Lelang Belum Dimulai
              </div>
              <div className="text-text-muted text-center px-6 mb-8">
                Silahkan klik pada tombol di bawah untuk memulai lelang
              </div>
              <Button
                className="w-[50%] py-3 shadow-md"
                disabled={uiState.startLoading}
                title={
                  <div className="flex items-center justify-center">
                    {uiState.startLoading ? (
                      <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Play className="w-6 h-6" />
                    )}
                    <span className="ml-2 font-semibold">
                      {uiState.startLoading ? "Memulai..." : "Mulai Lelang"}
                    </span>
                  </div>
                }
                onClick={() =>
                  showConfirmation(
                    "Apa Anda yakin mulai lelang sekarang?",
                    () => {
                      manageAuction("start");
                    },
                  )
                }
                aria-label="Mulai lelang"
              />
            </div>
          )}
          {auctionInfo.status !== AUCTION_STATUS.COMING && (
            <div className="grid grid-rows-2 gap-y-6 col-span-12 lg:col-span-6">
              {!auctionData.currentUnit ? (
                <div className="col-span-12 flex flex-col items-center justify-center border border-grey py-16 bg-white rounded-xl shadow-md">
                  <img
                    alt="waiting"
                    className="w-[50px] h-[90px] opacity-70"
                    src={require("../../assets/images/sand-timer.png")}
                  />
                  <div className="text-center mt-10 px-8">
                    <h3 className="text-2xl font-bold text-third mb-4">
                      Tidak Ada Unit Aktif
                    </h3>
                    <p className="text-text-muted text-sm mb-6 leading-relaxed">
                      Saat ini tidak ada unit yang sedang dilelang. Silakan
                      pilih unit dari daftar lot untuk memulai lelang.
                    </p>
                    {/* <div className="text-xs text-text-muted bg-white py-3 px-5 rounded-lg inline-block shadow-sm">
                      Proxy Bid dan Bidding akan tersedia setelah unit dipilih
                    </div> */}
                  </div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-12 gap-x-5">
                    <div className="col-span-6 card rounded-xl shadow-md border border-grey">
                      <div className="py-4 px-5 border-b border-border-divider font-bold text-third text-lg">
                        Proxy Bid
                      </div>
                      <div
                        className="overflow-y-scroll min-h-[240px] max-h-[350px]"
                        style={{ height: window.outerHeight - 600 }}
                      >
                        {auctionData.proxyBidder?.length === 0 ? (
                          <div className="flex flex-col items-center justify-center h-full py-12">
                            <p className="text-text-muted text-sm text-center">
                              Tidak ada Proxy Bid
                            </p>
                          </div>
                        ) : (
                          auctionData.proxyBidder?.map((proxy, idx) => (
                            <div
                              key={`proxy-${idx}`}
                              className="flex items-center justify-between px-5 py-4 text-md font-bold text-third border-b border-border-divider hover:bg-gray-50 transition-colors"
                            >
                              <label
                                className={"text-xs font-normal text-third"}
                              >
                                {proxy?.buyer_email}
                              </label>
                              <label className="text-md text-third font-bold">
                                {parseInt(proxy?.offer_price).toLocaleString()}
                              </label>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                    <div className="col-span-6 card rounded-xl shadow-md border border-grey">
                      <div className="py-4 px-5 border-b border-border-divider flex items-center justify-between">
                        <label className="font-bold text-third text-lg">
                          Bidding
                        </label>
                        <label className="py-1 px-3 bg-primary text-white rounded-lg text-sm font-semibold shadow-sm">
                          Lot-{auctionInfo.laneName}
                          {currentLotInfo.lotNumber}
                        </label>
                      </div>
                      <div
                        className="overflow-y-scroll min-h-[240px] max-h-[350px]"
                        style={{ height: window.outerHeight - 600 }}
                      >
                        {auctionData.bidder?.length === 0 ? (
                          <div className="flex flex-col items-center justify-center h-full py-12">
                            <p className="text-text-muted text-sm text-center">
                              Belum ada Bidding
                            </p>
                          </div>
                        ) : (
                          auctionData.bidder?.map((bidding, idx) => (
                            <div
                              key={`bidder-${idx}`}
                              className={`flex justify-between px-5 py-4 items-center border-b border-border-divider transition-colors ${
                                idx === 0
                                  ? "bg-bg-highlight"
                                  : "hover:bg-gray-50"
                              }`}
                            >
                              <label
                                className={`text-md font-bold ${
                                  idx === 0 ? "text-primary" : "text-third"
                                }`}
                              >
                                {bidding?.offer_price?.toLocaleString()}
                              </label>
                              <label className="text-xs text-third font-normal">
                                {bidding?.type === "Online Bidder"
                                  ? bidding?.buyer_email
                                  : bidding?.type}
                              </label>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-rows-5 gap-y-5">
                    <div className="grid grid-cols-12">
                      <div
                        className={`flex items-center justify-between col-span-12 rounded-xl bg-white shadow-md`}
                        role="timer"
                        aria-live="polite"
                        aria-label="Countdown timer"
                      >
                        <label
                          className={`text-base ml-5 font-semibold text-black py-4`}
                        >
                          Countdown
                        </label>
                        <label
                          className={`text-2xl mr-5 font-bold ${countdownColorClasses.textColor} py-4`}
                          aria-label={`${countdownDisplay.minutes} menit ${countdownDisplay.seconds} detik tersisa`}
                        >
                          {countdownDisplay.minutes}:{countdownDisplay.seconds}
                        </label>
                      </div>
                    </div>
                    <div className="grid grid-cols-12 row-span-4">
                      <div className="card col-span-12 px-8 items-center rounded-xl shadow-md border border-grey">
                        <div className="mb-6 mt-6 grid grid-cols-12 gap-x-3">
                          <div className="flex items-center col-span-12">
                            <div className="py-3 px-4 bg-bg-lighter border-2 border-border-medium rounded-l-xl font-semibold text-third">
                              Rp
                            </div>
                            <div
                              id="bid-amount"
                              className="py-3 px-4 bg-white border-2 border-l-0 border-border-medium rounded-r-xl w-full font-bold text-lg text-third"
                            >
                              {parseInt(auctionData.nextBid).toLocaleString()}
                            </div>
                          </div>
                        </div>
                        <Button
                          bgColor="bg-third"
                          className="py-4 w-full font-semibold text-base shadow-md animate-bounce-on-click"
                          title="Tawar"
                          onClick={() => executeAction("bid")}
                          disabled={
                            uiState.buttonDisabled || uiState.bidButtonCooldown
                          }
                          aria-label="Kirim tawaran"
                          aria-describedby="bid-amount"
                        />
                        <div className="flex items-center justify-center my-6 gap-4">
                          <Button
                            bgColor={`${
                              uiState.buttonDisabled ||
                              auctionData.bidder?.length === 0
                                ? "bg-slate-300"
                                : "bg-white"
                            }`}
                            color={`${
                              uiState.buttonDisabled ||
                              auctionData.bidder?.length === 0
                                ? "text-gray-400"
                                : "text-third"
                            }`}
                            className={`${
                              uiState.buttonDisabled ||
                              auctionData.bidder?.length === 0
                                ? "cursor-not-allowed"
                                : "hover:bg-gray-50"
                            } py-3 border-2 border-border-medium font-semibold shadow-sm`}
                            title="Floor"
                            onClick={() => executeAction("floor", true)}
                            disabled={
                              uiState.buttonDisabled ||
                              auctionData.bidder?.length === 0
                            }
                            aria-label="Set floor price"
                          />
                          <Button
                            bgColor={`${
                              uiState.isAutopilot ||
                              auctionData.bidder?.length > 0
                                ? "bg-slate-300"
                                : "bg-error"
                            }`}
                            color={`${
                              uiState.isAutopilot ||
                              auctionData.bidder?.length > 0
                                ? "text-gray-400"
                                : "text-white"
                            }`}
                            disabled={
                              uiState.isAutopilot ||
                              auctionData.bidder?.length > 0
                            }
                            className={`${
                              uiState.isAutopilot ||
                              auctionData.bidder?.length > 0
                                ? "cursor-not-allowed"
                                : "border-error"
                            } py-3 border-2 font-semibold shadow-sm`}
                            title="Belum Terjual"
                            onClick={() => executeAction("notSold", true)}
                            aria-label="Mark unit as not sold"
                          />
                          <Button
                            bgColor={`${
                              uiState.isAutopilot ||
                              auctionData.bidder?.length === 0
                                ? "bg-slate-300"
                                : "bg-success"
                            }`}
                            color={`${
                              uiState.isAutopilot ||
                              auctionData.bidder?.length === 0
                                ? "text-gray-400"
                                : "text-white"
                            }`}
                            disabled={
                              uiState.isAutopilot ||
                              auctionData.bidder?.length === 0
                            }
                            className={`${
                              uiState.isAutopilot ||
                              auctionData.bidder?.length === 0
                                ? "cursor-not-allowed"
                                : "hover:bg-green-600"
                            } py-3 border-2 border-border-medium font-semibold shadow-sm`}
                            title="Terjual"
                            onClick={() => onSold()}
                            aria-label="Mark unit as sold"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default V2LiveBiddingGo;
